import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Car, User, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { chatbotService } from '../services/api';
import { toast } from '../hooks/use-toast';
import { carBrands, carModels } from '../mock/mockData';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [showVehicleForm, setShowVehicleForm] = useState(true);
  const [vehicleInfo, setVehicleInfo] = useState({
    brand: '',
    model: '',
    year: '',
    fuel_type: ''
  });
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    mobile: '',
    email: ''
  });
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    // Generate or get session ID
    let existingSessionId = sessionStorage.getItem('chatbot_session_id');
    if (!existingSessionId) {
      existingSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('chatbot_session_id', existingSessionId);
    }
    setSessionId(existingSessionId);

    // Load existing conversation
    loadConversation(existingSessionId);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  const loadConversation = async (sid) => {
    try {
      const response = await chatbotService.getConversation(sid);
      if (response.conversation && response.conversation.messages) {
        const formattedMessages = response.conversation.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp
        }));
        setMessages(formattedMessages);

        // Check if vehicle info already collected
        if (response.conversation.vehicle_info && response.conversation.vehicle_info.brand) {
          setShowVehicleForm(false);
          setVehicleInfo(response.conversation.vehicle_info);
        }
        if (response.conversation.customer_name) {
          setCustomerInfo({
            name: response.conversation.customer_name,
            mobile: response.conversation.customer_mobile || ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const handleVehicleSubmit = () => {
    if (!vehicleInfo.brand || !vehicleInfo.model) {
      toast({
        title: 'Required Information',
        description: 'Please provide your car brand and model',
        variant: 'destructive'
      });
      return;
    }
    setShowVehicleForm(false);
    // Add a system message
    const welcomeMessage = {
      role: 'assistant',
      content: `Great! I see you have a ${vehicleInfo.brand} ${vehicleInfo.model}${vehicleInfo.year ? ` (${vehicleInfo.year})` : ''}. How can I help you today? Please describe any issues you're experiencing with your vehicle.`,
      timestamp: new Date()
    };
    setMessages([welcomeMessage]);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    if (showVehicleForm) {
      toast({
        title: 'Vehicle Information Required',
        description: 'Please provide your vehicle details first',
        variant: 'destructive'
      });
      return;
    }

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await chatbotService.sendMessage({
        session_id: sessionId,
        message: inputMessage,
        vehicle_brand: vehicleInfo.brand,
        vehicle_model: vehicleInfo.model,
        vehicle_year: vehicleInfo.year ? parseInt(vehicleInfo.year) : null,
        vehicle_fuel_type: vehicleInfo.fuel_type,
        customer_name: customerInfo.name || null,
        customer_mobile: customerInfo.mobile || null,
        customer_email: customerInfo.email || null
      });

      const aiMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
        showBookingButton: response.show_booking_button,
        recommendedService: response.recommended_service
      };

      setMessages(prev => [...prev, aiMessage]);

      // Show suggested services as a toast
      if (response.suggested_services && response.suggested_services.length > 0) {
        toast({
          title: 'Suggested Services',
          description: response.suggested_services.join(', ')
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all duration-300 animate-bounce"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 h-[600px] shadow-2xl rounded-lg overflow-hidden">
          <Card className="h-full flex flex-col">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  <CardTitle className="text-lg">Car Diagnostic Assistant</CardTitle>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="hover:bg-white/20 rounded-full p-1 transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-blue-100 mt-1">AI-powered car problem diagnosis</p>
            </CardHeader>

            <CardContent className="flex-1 overflow-hidden p-0 flex flex-col">
              {/* Vehicle Info Form */}
              {showVehicleForm && (
                <div className="p-4 bg-blue-50 border-b space-y-3">
                  <p className="text-sm font-medium text-gray-700">Tell us about your vehicle:</p>
                  
                  <div className="space-y-2">
                    <label className="block text-xs font-medium">Car Brand *</label>
                    <Select 
                      value={vehicleInfo.brand} 
                      onValueChange={(value) => setVehicleInfo({ ...vehicleInfo, brand: value, model: '' })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {carBrands.map(brand => (
                          <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs font-medium">Car Model *</label>
                    <Select 
                      value={vehicleInfo.model} 
                      onValueChange={(value) => setVehicleInfo({ ...vehicleInfo, model: value })}
                      disabled={!vehicleInfo.brand}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicleInfo.brand && carModels[vehicleInfo.brand]?.map(model => (
                          <SelectItem key={model} value={model}>{model}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <label className="block text-xs font-medium">Fuel Type</label>
                      <Select 
                        value={vehicleInfo.fuel_type} 
                        onValueChange={(value) => setVehicleInfo({ ...vehicleInfo, fuel_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Fuel" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Petrol">Petrol</SelectItem>
                          <SelectItem value="Diesel">Diesel</SelectItem>
                          <SelectItem value="CNG">CNG</SelectItem>
                          <SelectItem value="Electric">Electric</SelectItem>
                          <SelectItem value="Hybrid">Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-medium">Year</label>
                      <Input
                        type="number"
                        placeholder="e.g., 2020"
                        value={vehicleInfo.year}
                        onChange={(e) => setVehicleInfo({ ...vehicleInfo, year: e.target.value })}
                        min="1990"
                        max={new Date().getFullYear()}
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={handleVehicleSubmit}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Start Chat
                  </Button>
                </div>
              )}

              {/* Messages Area */}
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 && !showVehicleForm && (
                  <div className="text-center text-gray-500 text-sm mt-8">
                    <Car className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>Describe your car problem</p>
                    <p className="text-xs mt-1">Our AI will help diagnose and suggest solutions</p>
                  </div>
                )}

                {messages.map((msg, index) => (
                  <div key={index}>
                    <div
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                    
                    {/* Show booking button for AI messages with booking intent */}
                    {msg.role === 'assistant' && msg.showBookingButton && (
                      <div className="flex justify-start mt-2">
                        <Button
                          onClick={() => {
                            // Store recommended service in sessionStorage
                            if (msg.recommendedService) {
                              sessionStorage.setItem('recommended_service', msg.recommendedService);
                            }
                            // Store vehicle info for form pre-fill
                            sessionStorage.setItem('chatbot_vehicle_info', JSON.stringify(vehicleInfo));
                            // Navigate to booking page
                            window.location.href = '/services';
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          📋 Book Service Now
                        </Button>
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg p-3">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              {!showVehicleForm && (
                <div className="p-4 border-t bg-white">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Describe your car problem..."
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={isLoading}
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={isLoading || !inputMessage.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
